import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Folder,
  FileText,
  ChevronRight,
  Loader2,
  Paperclip,
  Search,
  Home,
} from "lucide-react";
import { foldersApi, documentsApi } from "../../services/api";
import { ComposeAttachment } from "../../contexts/EmailComposeContext";

// ── Types ──────────────────────────────────────────────────────────

interface DocVersion {
  _id: string;
  file?: { filename: string; mimeType: string; sizeBytes: number };
  storage?: { key: string };
}

interface DocItem {
  _id: string;
  title: string;
  type: string;
  createdAt: string;
  currentVersionId?: DocVersion;
}

interface FolderItem {
  _id: string;
  name: string;
  color?: string;
  clientId?: string;
  matterId?: string;
  itemCount?: number;
}

interface DocumentPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (attachment: ComposeAttachment) => void;
  clientId?: string | null;
}

// ── Helpers ─────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIconColor = (mimeType?: string) => {
  if (!mimeType) return "text-slate-400";
  if (mimeType.includes("pdf")) return "text-red-500";
  if (mimeType.includes("word") || mimeType.includes("document")) return "text-blue-500";
  if (mimeType.includes("sheet") || mimeType.includes("excel")) return "text-emerald-500";
  if (mimeType.includes("image")) return "text-purple-500";
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "text-orange-500";
  return "text-slate-400";
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ── Component ───────────────────────────────────────────────────────

const DocumentPickerModal: React.FC<DocumentPickerModalProps> = ({
  open,
  onClose,
  onSelect,
  clientId,
}) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [documents, setDocuments] = useState<DocItem[]>([]);
  // Stack-based breadcrumb (same pattern as DocumentsPage)
  const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);
  const [folderPath, setFolderPath] = useState<FolderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<DocItem[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const seededRef = useRef(false);

  // ── Fetch folders for current level ──────────────────────────────

  const fetchFolders = useCallback(async (folder: FolderItem | null) => {
    try {
      const params: Record<string, any> = {
        parentId: folder?._id || "root",
      };
      const res = await foldersApi.list(params);
      setFolders(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load folders:", err);
      setFolders([]);
    }
  }, []);

  // ── Fetch documents for current folder ───────────────────────────

  const fetchDocuments = useCallback(async (folder: FolderItem | null) => {
    try {
      if (!folder) {
        // At root level, don't show unfiled documents
        setDocuments([]);
        return;
      }
      const params: Record<string, any> = { folderId: folder._id, limit: 50 };
      const res = await documentsApi.list(params);
      const docs = res.data?.data || res.data || [];
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to load documents:", err);
      setDocuments([]);
    }
  }, []);

  // ── Load both folders + documents for a level ────────────────────

  const loadLevel = useCallback(async (folder: FolderItem | null) => {
    setLoading(true);
    await Promise.all([fetchFolders(folder), fetchDocuments(folder)]);
    setLoading(false);
  }, [fetchFolders, fetchDocuments]);

  // ── Navigate into a folder ───────────────────────────────────────

  const navigateToFolder = useCallback(
    (folder: FolderItem) => {
      setSearchQuery("");
      setSearchResults(null);
      setFolderPath((prev) => [...prev, folder]);
      setCurrentFolder(folder);
      loadLevel(folder);
    },
    [loadLevel]
  );

  // ── Navigate via breadcrumb ──────────────────────────────────────

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      setSearchQuery("");
      setSearchResults(null);
      if (index < 0) {
        // Go to root
        setFolderPath([]);
        setCurrentFolder(null);
        loadLevel(null);
      } else {
        const target = folderPath[index];
        setFolderPath((prev) => prev.slice(0, index + 1));
        setCurrentFolder(target);
        loadLevel(target);
      }
    },
    [folderPath, loadLevel]
  );

  // ── Search documents ─────────────────────────────────────────────

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const params: Record<string, any> = { search: searchQuery, limit: 30 };
        const res = await documentsApi.list(params);
        const docs = res.data?.data || res.data || [];
        setSearchResults(docs);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // ── Initial load: seed, then navigate to client folder or root ──

  useEffect(() => {
    if (!open) return;

    const init = async () => {
      setLoading(true);
      setSearchQuery("");
      setSearchResults(null);
      setFolderPath([]);
      setCurrentFolder(null);
      setFolders([]);
      setDocuments([]);

      // Seed default folders (ensures Clients folder structure exists)
      if (!seededRef.current) {
        try {
          await foldersApi.seed();
        } catch {
          // Seed may fail if already seeded, that's fine
        }
        seededRef.current = true;
      }

      // If we have a clientId, try to find their folder and navigate there
      if (clientId) {
        try {
          // Find the client's folder (flat search ignores parentId filter)
          const res = await foldersApi.list({ clientId, flat: "true" });
          const clientFolders = (res.data?.data || res.data || []).filter(
            (f: any) => !f.matterId
          );

          if (clientFolders.length > 0) {
            const clientFolder = clientFolders[0];
            // Build the path from root to this folder using ancestors
            try {
              const ancestorsRes = await foldersApi.getAncestors(clientFolder._id);
              const ancestors: FolderItem[] = ancestorsRes.data?.data || ancestorsRes.data || [];
              // ancestors = [root, ..., parent]  -  does NOT include current folder
              const path = [...ancestors, clientFolder];
              setFolderPath(path);
              setCurrentFolder(clientFolder);
              await Promise.all([
                fetchFolders(clientFolder),
                fetchDocuments(clientFolder),
              ]);
              setLoading(false);
              return;
            } catch {
              // Fall through to root
            }
          }
        } catch {
          // Fall through to root
        }
      }

      // Default: load root level
      await Promise.all([fetchFolders(null), fetchDocuments(null)]);
      setLoading(false);
    };

    init();
  }, [open, clientId, fetchFolders, fetchDocuments]);

  // ── Select document ──────────────────────────────────────────────

  const handleSelectDocument = useCallback(
    (doc: DocItem) => {
      const version = doc.currentVersionId;
      if (!version?.storage?.key || !version?.file) return;
      onSelect({
        key: version.storage.key,
        filename: version.file.filename,
        mimeType: version.file.mimeType,
        sizeBytes: version.file.sizeBytes,
      });
      onClose();
    },
    [onSelect, onClose]
  );

  // ── Escape key ───────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const docsToShow = searchResults ?? documents;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[1100]"
            onClick={onClose}
          />
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed inset-0 z-[1100] flex items-center justify-center p-6"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl flex flex-col overflow-hidden" style={{ height: "80vh" }}>
              {/* ── Header ── */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: "var(--color-primary)" }}
                  >
                    <Paperclip className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">
                      Attach from Documents
                    </h3>
                    <p className="text-xs text-slate-400">
                      Browse your files and select a document to attach
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* ── Search + Breadcrumbs bar ── */}
              <div className="px-6 py-3 border-b border-slate-100 flex-shrink-0 space-y-2.5">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search all documents..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-slate-50"
                  />
                </div>

                {/* Breadcrumbs  -  always show */}
                {!searchResults && (
                  <div className="flex items-center gap-1.5 text-sm min-w-0 overflow-x-auto">
                    <button
                      onClick={() => navigateToBreadcrumb(-1)}
                      className={`flex items-center gap-1 flex-shrink-0 transition-colors ${
                        folderPath.length === 0
                          ? "text-slate-700 font-medium"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      <Home className="h-3.5 w-3.5" />
                      <span>Documents</span>
                    </button>
                    {folderPath.map((folder, idx) => (
                      <React.Fragment key={folder._id}>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
                        <button
                          onClick={() => navigateToBreadcrumb(idx)}
                          className={`flex-shrink-0 transition-colors truncate max-w-[160px] ${
                            idx === folderPath.length - 1
                              ? "text-slate-700 font-medium"
                              : "text-slate-400 hover:text-slate-600"
                          }`}
                        >
                          {folder.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Content area ── */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {loading || searchLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-7 w-7 text-slate-300 animate-spin" />
                  </div>
                ) : (
                  <div className="py-1">
                    {/* Folders (hidden during search) */}
                    {!searchResults &&
                      folders.map((folder) => (
                        <button
                          key={folder._id}
                          onClick={() => navigateToFolder(folder)}
                          className="w-full flex items-center gap-3.5 px-6 py-3 hover:bg-slate-50 transition-colors text-left group"
                        >
                          <div className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors">
                            <Folder className="h-5 w-5 text-amber-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {folder.name}
                            </p>
                            {folder.itemCount != null && (
                              <p className="text-[11px] text-slate-400">
                                {folder.itemCount} item{folder.itemCount !== 1 ? "s" : ""}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" />
                        </button>
                      ))}

                    {/* Divider between folders and documents */}
                    {!searchResults && folders.length > 0 && docsToShow.length > 0 && (
                      <div className="mx-6 my-1 border-t border-slate-100" />
                    )}

                    {/* Documents */}
                    {docsToShow.map((doc) => {
                      const version = doc.currentVersionId;
                      const hasFile = version?.storage?.key && version?.file;
                      return (
                        <button
                          key={doc._id}
                          onClick={() => hasFile && handleSelectDocument(doc)}
                          disabled={!hasFile}
                          className={`w-full flex items-center gap-3.5 px-6 py-3 transition-colors text-left group ${
                            hasFile
                              ? "hover:bg-emerald-50/60 cursor-pointer"
                              : "opacity-40 cursor-not-allowed"
                          }`}
                        >
                          <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                            <FileText
                              className={`h-5 w-5 ${fileIconColor(version?.file?.mimeType)}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {doc.title || version?.file?.filename || "Untitled"}
                            </p>
                            <p className="text-[11px] text-slate-400 flex items-center gap-2">
                              <span className="truncate">{version?.file?.filename}</span>
                              {version?.file?.sizeBytes ? (
                                <span className="flex-shrink-0">
                                  {formatFileSize(version.file.sizeBytes)}
                                </span>
                              ) : null}
                              {doc.createdAt && (
                                <span className="flex-shrink-0">{formatDate(doc.createdAt)}</span>
                              )}
                            </p>
                          </div>
                          {hasFile && (
                            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              Attach
                            </span>
                          )}
                        </button>
                      );
                    })}

                    {/* Empty state */}
                    {!loading &&
                      !searchLoading &&
                      folders.length === 0 &&
                      docsToShow.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                          <div className="h-14 w-14 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-4">
                            <FileText className="h-7 w-7 text-slate-300" />
                          </div>
                          <p className="text-sm font-medium text-slate-500 mb-1">
                            {searchResults !== null
                              ? "No documents found"
                              : "This folder is empty"}
                          </p>
                          <p className="text-xs text-slate-400">
                            {searchResults !== null
                              ? "Try a different search term"
                              : "Navigate to another folder using the breadcrumbs above"}
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DocumentPickerModal;
